import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["accessibilityLabel", "checked", "children", "defaultValue", "description", "disableClickableLabel", "disabled", "error", "focused", "highlighted", "hovered", "id", "isIndeterminate", "keyboardFocused", "legal", "name", "onBlur", "onChange", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "testID", "title", "warning", "UNSAFE_containerProps", "UNSAFE_inputProps"],
  _excluded2 = ["onBlur", "onFocus"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback } from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import SpacerVertical from '@bubble-ui/atom-spacer-vertical';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import useUncontrolledProp from '@bubble-ui/hook-use-uncontrolled-prop';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import ActionIconCheckmark from '@bubble-ui/icon-action-checkmark';
import ActionIconMinus from '@bubble-ui/icon-action-minus';
import Text from '@bubble-ui/primitive-text';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { useUniqueFormIdentifier } from '@bubble-ui/hook-use-unique-form-identifier';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultDesignTokens } from './defaultDesignTokens';
import { getIds } from './getIds';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
var SwitchCheckbox = _ref => {
  var {
      accessibilityLabel,
      checked,
      children,
      defaultValue,
      description,
      disableClickableLabel,
      disabled,
      error,
      focused,
      highlighted,
      hovered,
      id,
      isIndeterminate,
      keyboardFocused,
      legal,
      name,
      onBlur: onBlurProp,
      onChange,
      onFocus: onFocusProp,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed,
      testID,
      title,
      warning,
      UNSAFE_containerProps,
      UNSAFE_inputProps
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'SwitchCheckbox',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var ids = getIds(id);
  var designContext = useDesignContext(defaultDesignTokens);
  var designVersion = useDesignVersion();
  var [interactiveStateCombined, _ref2, {
    activateMouseFocus
  }] = useInteractiveStateCombined(_objectSpread(_objectSpread({}, props), {}, {
    disabled,
    focused,
    hovered,
    onBlur: onBlurProp,
    onFocus: onFocusProp,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed,
    keyboardFocused
  }));
  var {
      onBlur,
      onFocus
    } = _ref2,
    eventHandlers = _objectWithoutProperties(_ref2, _excluded2);
  var [uncontrolledValue, setUncontrolledValue] = useUncontrolledProp({
    prop: checked,
    defaultProp: defaultValue || false,
    onChange
  });
  var componentName = useUniqueFormIdentifier({
    name,
    fallbackName: 'SwitchCheckbox'
  });
  var uniqueComponentId = useUniqueFormIdentifier({
    fallbackName: name || 'SwitchCheckbox'
  });
  var uniqueTextContentId = useUniqueFormIdentifier({
    fallbackName: 'SwitchCheckboxLabel'
  });
  var componentId = (ids === null || ids === void 0 ? void 0 : ids.input) || uniqueComponentId;
  var {
    main,
    mainWrapper,
    inputStyle,
    labelStyle,
    overlayStyle,
    boxStyle,
    checkmarkWrapperStyle,
    iconWrapperStyle,
    iconColor,
    checkmarkTickStyle,
    textStyle,
    titleStyle,
    titleTextStyle,
    descriptionTextStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(SwitchCheckbox, getDefaultStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
    accessibilityLabel,
    checked: uncontrolledValue,
    children,
    defaultValue,
    description,
    designContext,
    designVersion,
    disableClickableLabel,
    disabled,
    error,
    focused,
    highlighted,
    hovered,
    id,
    isIndeterminate,
    keyboardFocused,
    legal,
    name,
    onBlur,
    onChange,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed,
    testID,
    title,
    warning,
    UNSAFE_containerProps,
    UNSAFE_inputProps
  }, interactiveStateCombined));
  var onLabelClick = useCallback(e => {
    var _document$getElementB;
    e.preventDefault && e.preventDefault();
    (_document$getElementB = document.getElementById(componentId)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.focus();
    activateMouseFocus();
    setUncontrolledValue(!uncontrolledValue, e);
  }, [activateMouseFocus, componentId, setUncontrolledValue, uncontrolledValue]);
  var textContentId = ids !== null && ids !== void 0 && ids.text ? ids === null || ids === void 0 ? void 0 : ids.text : disableClickableLabel ? uniqueTextContentId : undefined;
  var switchCheckboxTextChildren = title !== undefined && description !== undefined ? /*#__PURE__*/React.createElement("div", {
    id: textContentId,
    style: titleStyle
  }, /*#__PURE__*/React.createElement(Text, {
    id: ids === null || ids === void 0 ? void 0 : ids.title,
    style: titleTextStyle
  }, title), /*#__PURE__*/React.createElement(SpacerVertical, {
    spaceToken: "space/200"
  }), /*#__PURE__*/React.createElement(Text, {
    id: ids === null || ids === void 0 ? void 0 : ids.description,
    style: descriptionTextStyle
  }, description)) : /*#__PURE__*/React.createElement("span", {
    id: textContentId,
    style: textStyle
  }, children);
  return /*#__PURE__*/React.createElement("div", _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root
  }, UNSAFE_containerProps, {
    style: mainWrapper
  }, props), /*#__PURE__*/React.createElement("div", {
    style: main
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: componentId
  }, UNSAFE_inputProps, {
    name: componentName,
    "aria-label": accessibilityLabel,
    "aria-labelledby": accessibilityLabel ? undefined : textContentId,
    type: "checkbox",
    checked: uncontrolledValue,
    disabled: disabled,
    onChange: disabled ? undefined : e => setUncontrolledValue(!uncontrolledValue, e),
    style: inputStyle,
    onFocus: onFocus,
    onBlur: onBlur
  })), /*#__PURE__*/React.createElement("label", _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.label,
    htmlFor: componentId,
    style: labelStyle
  }, eventHandlers, {
    onClick: onLabelClick
  }), /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    id: ids === null || ids === void 0 ? void 0 : ids.overlay,
    style: overlayStyle
  }), /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    id: ids === null || ids === void 0 ? void 0 : ids.box,
    style: boxStyle
  }), isIndeterminate ? /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    id: ids === null || ids === void 0 ? void 0 : ids.minusWrapper,
    style: iconWrapperStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(ActionIconMinus, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.minus
  }, iconColor)))) : uncontrolledValue && /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    id: ids === null || ids === void 0 ? void 0 : ids.checkmarkWrapper,
    style: checkmarkWrapperStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(ActionIconCheckmark, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.checkmark
  }, checkmarkTickStyle)))), !disableClickableLabel && switchCheckboxTextChildren), disableClickableLabel && switchCheckboxTextChildren));
};
SwitchCheckbox.displayName = 'SwitchCheckbox';
export default withProvisionalOverrideFromContext(SwitchCheckbox);
//# sourceMappingURL=index.js.map